/**
 * Custom Redux Hooks for Authentication
 *
 * Provides typed access to auth state and actions.
 * Avoids repetitive useSelector/useDispatch calls in components.
 */

import { useDispatch, useSelector } from 'react-redux';
import type { RootState, AppDispatch } from '../../../App/store';
import { setUser, setToken, logout, clearError } from '../slices/authSlice';

/**
 * Custom hook to access auth state
 * Returns all auth-related state properties
 */
export const useAuthState = () => {
  return useSelector((state: RootState) => state.auth);
};

/**
 * Custom hook to access specific auth user
 * Returns current logged-in user or null
 */
export const useAuthUser = () => {
  return useSelector((state: RootState) => state.auth.user);
};

/**
 * Custom hook to check if user is authenticated
 * Returns true if logged in, false otherwise
 */
export const useIsAuthenticated = () => {
  return useSelector((state: RootState) => state.auth.isAuthenticated);
};

/**
 * Custom hook to access auth token
 * Returns JWT token string or null
 */
export const useAuthToken = () => {
  return useSelector((state: RootState) => state.auth.token);
};

/**
 * Custom hook to access auth loading state
 * Returns true while login/logout is in progress
 */
export const useAuthLoading = () => {
  return useSelector((state: RootState) => state.auth.loading);
};

/**
 * Custom hook to access auth error
 * Returns error message or null
 */
export const useAuthError = () => {
  return useSelector((state: RootState) => state.auth.error);
};

/**
 * Custom hook to check if password change is required
 * Returns true if user must change password before accessing protected routes
 */
export const usePasswordChangeRequired = () => {
  return useSelector((state: RootState) => state.auth.passwordChangeRequired);
};

/**
 * Custom hook to access auth dispatch actions
 * Returns object with logout and clearError functions
 */
export const useAuthActions = () => {
  const dispatch = useDispatch<AppDispatch>();

  return {
    setUser: (user: any) => dispatch(setUser(user)),
    setToken: (token: string | null) => dispatch(setToken(token)),
    logout: () => dispatch(logout()),
    clearError: () => dispatch(clearError()),
  };
};
